/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import get from 'lodash/get';
import isArray from 'lodash/isArray';
import URI from 'urijs';

import { errorWithLevelWarning } from '../../error/error-with-level';
import { fetchWithTimeout } from '../../fetch/fetch';
import CommunicatorFormat from '../format/communicator-format';
import CommunicatorUtility from '../utility/communicator-utility';

// TODO: Statizovat zrušením konstruktoru a předáváním URL do fetch(url).
// TODO: Opravit pomýlené typy ohledně volitelnosti.
/**
 * Komunikátor zařízení s manifestem verze.
 */
class CommunicatorManifest {
  /**
   * Vytvoří komunikátor s manifestem verze.
   * @param {string} url URL adresa pro stažení manifestu.
   */
  constructor(url) {
    if (arguments.length < 1) {
      throw new TypeError('Missing arguments.');
    }
    this.url = url;
  }

  /**
   * Doručí informace o verzi.
   * @returns {Promise<Object>} Informace o verzi a jejích sestavení.
   */
  fetch() {
    const { url } = this;
    return (
      fetchWithTimeout(url, { method: 'GET' })
        // Snížení priority logování pro chyby síťového spojení.
        .catch((error) => {
          throw errorWithLevelWarning(error);
        })
        .then((response) => {
          // Snížení priority logování pro chyby ze serveru.
          if (!response.ok) {
            throw errorWithLevelWarning(
              new RangeError(`Invalid HTTP status "${response.status}".`)
            );
          }
          return response.json();
        })
        .then((json) => CommunicatorManifest.parse(json, url))
    );
  }

  /**
   * Zpracuje informace o verzi.
   * @param {Object | null | undefined} json Odpověď na manifest verze.
   * @param {string} url URL adresa manifestu verze.
   * @returns {Object} Informace o verzi a jejích sestavení.
   */
  static parse(json, url) {
    if (arguments.length < 2) {
      throw new TypeError('Missing arguments.');
    }
    let buildArray;
    const builds = get(json, 'builds');
    if (isArray(builds)) {
      buildArray = builds.map((build) =>
        CommunicatorManifest.parseBuild(build, url)
      );
    } else {
      buildArray = [];
    }
    return {
      buildArray,
      version: CommunicatorUtility.parseStringOrNull(get(json, 'appVersion'))
    };
  }

  /**
   * Zpracuje informace o jednom sestavení aplikace.
   * @param {Object} json Část odpověďi manifestu verze s jedním sestavením.
   * @param {string} url URL adresa manifestu verze.
   * @returns {Object} Informace o sestavení.
   */
  static parseBuild(json, url) {
    if (arguments.length < 2) {
      throw new TypeError('Missing arguments.');
    }
    return {
      buildId: json.buildId,
      formatEnum: CommunicatorFormat.parse(json.type),
      updatePlatform: json.platform,
      url: URI(json.url).absoluteTo(url).toString()
    };
  }
}

export default CommunicatorManifest;
